// US-CHORE-01 -- guardian creates a chore for a linked child.

import { type AppError, type ChoreEntry, accountsList, choreCreate } from '@chorepop/core/client';
import { type Component, For, Show, createEffect, createResource, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';
import { ToggleSwitch } from '../components/ToggleSwitch';

type Props = {
  sessionId: string;
  childAccountId: number;
  childDisplayName: string;
  currencyCode: string;
  /** When true, the child chooser starts on its explicit "Any child"
   * shared-pick-up option. Used by the global shared-chore entry point. */
  defaultAnyChild?: boolean;
  onCreated: (result: ChoreEntry) => void;
  onCancel: () => void;
};

export const CreateChoreScreen: Component<Props> = (props) => {
  const [familyAccounts] = createResource(async () => {
    const result = await accountsList();
    if (!result.ok) throw new Error(result.error.message);
    return result.value.filter((account) => account.roleCode === 'child');
  });

  const [name, setName] = createSignal('');
  const [description, setDescription] = createSignal('');
  const [rewardInput, setRewardInput] = createSignal('');
  const [submitting, setSubmitting] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);
  const [requiresApproval, setRequiresApproval] = createSignal(false);
  const [selectedChildAccountId, setSelectedChildAccountId] = createSignal<number | 'all' | 'any'>(
    props.defaultAnyChild ? 'any' : props.childAccountId,
  );
  const allChildren = () => selectedChildAccountId() === 'all';
  const anyChild = () => selectedChildAccountId() === 'any';

  // The generic "New chore" entry point is not tied to an individual child.
  // Select a real child as soon as the family list loads. Keeping this decision
  // in the form prevents a guardian account id from accidentally being sent to
  // the child-only backend command.
  createEffect(() => {
    const children = familyAccounts();
    const selected = selectedChildAccountId();
    if (!children?.length || selected === 'all' || selected === 'any') return;
    if (!children.some((child) => child.accountId === selected)) {
      const firstChild = children[0];
      if (firstChild) setSelectedChildAccountId(firstChild.accountId);
    }
  });

  const parseRewardMinorUnits = (): number | null => {
    const raw = rewardInput().trim();
    if (raw === '') return 0; // Zero-reward chores are allowed.
    if (!/^\d+(\.\d{1,2})?$/.test(raw)) return null;
    const major = Number.parseFloat(raw);
    if (!Number.isFinite(major) || major < 0) return null;
    return Math.round(major * 100);
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setErrorMessage(null);

    if (name().trim() === '') {
      setErrorMessage('Chore name is required.');
      return;
    }

    const reward = parseRewardMinorUnits();
    if (reward === null) {
      setErrorMessage('Enter a valid reward (e.g. 0, 1, or 2.50).');
      return;
    }

    if (!anyChild()) {
      if (familyAccounts.loading) {
        setErrorMessage('Your family members are still loading. Please try again in a moment.');
        return;
      }
      if (familyAccounts.error) {
        setErrorMessage('Could not load your children. Please go back and try again.');
        return;
      }
      if (!(familyAccounts()?.length ?? 0)) {
        setErrorMessage('Add a child before creating a chore for one child.');
        return;
      }
    }

    setSubmitting(true);
    try {
      const trimmedDescription = description().trim();
      const selectedChild = selectedChildAccountId();
      const childIds: Array<number | null> = allChildren()
        ? (familyAccounts() ?? []).map((child) => child.accountId)
        : anyChild()
          ? [null]
          : typeof selectedChild === 'number'
            ? [selectedChild]
            : [];
      let firstCreated: ChoreEntry | null = null;
      for (const childAccountId of childIds) {
        const result = await choreCreate({
          sessionId: props.sessionId,
          childAccountId,
          choreName: name().trim(),
          choreTypeCode: 'a_la_carte',
          rewardMinorUnits: reward,
          // Approval-before-pickup only belongs to the shared Any child list.
          ...(anyChild() && requiresApproval() ? { requiresApprovalToClaim: true } : {}),
          ...(trimmedDescription === '' ? {} : { choreDescription: trimmedDescription }),
        });
        if (!result.ok) {
          setErrorMessage(
            allChildren()
              ? `The chore was saved for some children, but not all: ${humanReadableError(result.error)}`
              : humanReadableError(result.error),
          );
          return;
        }
        firstCreated ??= result.value;
      }
      if (firstCreated) props.onCreated(firstCreated);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActionScreen
      eyebrow={
        anyChild()
          ? 'Any child can pick this'
          : allChildren()
            ? 'For every child'
            : 'Choose a child'
      }
      title="Add a chore"
      onBack={props.onCancel}
      footer={
        <button
          type="submit"
          form="create-chore-form"
          class="primary action-screen-primary"
          disabled={
            submitting() ||
            (!anyChild() &&
              (familyAccounts.loading ||
                Boolean(familyAccounts.error) ||
                !(familyAccounts()?.length ?? 0)))
          }
        >
          {submitting() ? 'Saving…' : 'Create chore'}
        </button>
      }
    >
      <p class="action-screen-intro">
        {anyChild()
          ? "Add a chore any of your children can pick up. You'll approve completions before the reward lands in their buckets."
          : allChildren()
            ? 'Create a separate copy of this chore for every child in your family. Each child sees it as their own job.'
            : "Choose which child this chore is for. You'll approve completions before the reward lands in their buckets."}
      </p>

      <form id="create-chore-form" onSubmit={handleSubmit} novalidate class="action-form">
        <label class="field">
          <span class="field-label">Assign to</span>
          <select
            value={selectedChildAccountId()}
            disabled={
              submitting() ||
              familyAccounts.loading ||
              Boolean(familyAccounts.error) ||
              !(familyAccounts()?.length ?? 0)
            }
            onChange={(e) => {
              const selection = e.currentTarget.value;
              setSelectedChildAccountId(
                selection === 'all' || selection === 'any' ? selection : Number(selection),
              );
              if (selection !== 'any') setRequiresApproval(false);
              setErrorMessage(null);
            }}
            data-testid="create-chore-child-select"
          >
            <Show when={familyAccounts.loading}>
              <option value="">Loading children…</option>
            </Show>
            <For each={familyAccounts() ?? []}>
              {(child) => <option value={child.accountId}>{child.displayName}</option>}
            </For>
            <option value="all">All children — make one for each child</option>
            <option value="any">Any child — shared pick-up chore</option>
          </select>
          <Show when={!familyAccounts.loading && !(familyAccounts()?.length ?? 0)}>
            <span class="field-hint">
              {familyAccounts.error
                ? 'Could not load children. Go back and try again.'
                : 'Add a child before creating a chore.'}
            </span>
          </Show>
        </label>
        <Show when={allChildren()}>
          <p class="field-hint" style="margin: -8px 0 var(--space-3);">
            This creates one assigned chore for every active child. It is not a shared list.
          </p>
        </Show>
        <Show when={anyChild()}>
          <p class="field-hint" style="margin: -8px 0 var(--space-3);">
            This creates one shared chore. Any of your children will see it on their "Available
            chores" tab and can pick it up.
          </p>
          <ToggleSwitch
            label="Requires my approval before a child can start working on this"
            checked={requiresApproval()}
            disabled={submitting()}
            onChange={(enabled) => {
              setRequiresApproval(enabled);
              setErrorMessage(null);
            }}
            testId="create-chore-requires-approval"
          />
          <Show when={requiresApproval()}>
            <p class="field-hint" style="margin: -8px 0 var(--space-3); padding-left: 28px;">
              When a child picks the chore, you'll see it in your pending-approvals queue and can
              approve or reject the pick. Use this for chores you only want a specific child to do
              (e.g. "mow the lawn" for an older sibling).
            </p>
          </Show>
        </Show>

        <label class="field">
          <span class="field-label">Chore name</span>
          <input
            type="text"
            autocomplete="off"
            required
            autofocus
            maxlength="120"
            placeholder="e.g. Take out the bins"
            disabled={submitting()}
            value={name()}
            onInput={(e) => {
              setName(e.currentTarget.value);
              setErrorMessage(null);
            }}
          />
        </label>

        <label class="field">
          <span class="field-label">Reward ({props.currencyCode})</span>
          <input
            type="text"
            inputmode="decimal"
            autocomplete="off"
            placeholder="0.00"
            disabled={submitting()}
            value={rewardInput()}
            onInput={(e) => {
              setRewardInput(e.currentTarget.value);
              setErrorMessage(null);
            }}
          />
          <span class="field-hint">Leave blank or enter 0 for chores without a reward.</span>
        </label>

        <label class="field">
          <span class="field-label">Description (optional)</span>
          <textarea
            class="field-textarea"
            autocomplete="off"
            maxlength="500"
            placeholder="Any extra detail -- instructions, supplies, where to look. Wraps freely; the field scrolls if you write more than fits."
            rows="5"
            disabled={submitting()}
            value={description()}
            onInput={(e) => setDescription(e.currentTarget.value)}
          />
        </label>

        <Show when={errorMessage()}>
          {(msg) => (
            <div class="form-error" role="alert">
              {msg()}
            </div>
          )}
        </Show>
      </form>
    </ActionScreen>
  );
};

function humanReadableError(error: AppError): string {
  switch (error.kind) {
    case 'validation':
      return error.message;
    case 'unauthorized':
      return 'Your session has expired or you are not allowed to create this chore.';
    case 'not_found':
      return 'That child account no longer exists.';
    case 'network_required':
      return error.message;
    default:
      return `Something went wrong: ${error.message}`;
  }
}
